import { shouldSubscribeSpatiusViewerPublication } from "@/hooks/spatius-primary-session-policy";
import { collectActiveRemoteAudioPublications } from "@/lib/livekit-duplicate-audio-guard";
import { createLogger } from "@/lib/logger";
import {
  cleanupSpatiusSdkAudioElements,
  installSpatiusSdkAudioElementGuard,
  silenceSpatiusSdkAudioElementsForLipSync,
} from "@/lib/spatius-sdk-audio-cleanup";
import { useSession } from "@livekit/components-react";
import {
  AvatarManager,
  AvatarSDK,
  ConnectionState as AvatarSdkConnectionState,
  ConversationState as AvatarSdkConversationState,
  LogLevel as AvatarSdkLogLevel,
  AvatarView,
  DrivingServiceMode,
  LoadProgress,
} from "@spatius/avatarkit";
import { AvatarPlayer, LiveKitProvider } from "@spatius/avatarkit-rtc";
import type { AvatarPlayerOptions } from "@spatius/avatarkit-rtc";
import { RoomEvent, TokenSource, Track } from "livekit-client";
import type { Room } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  SpatiusAvatarConnectionStatus,
  SpatiusAvatarState,
  UseSpatiusAvatarOptions,
  UseSpatiusAvatarResult,
} from "../types/spatius-avatar";

const logger = createLogger("useSpatiusAvatar");

function toError(error: unknown, fallbackMessage: string) {
  return error instanceof Error ? error : new Error(fallbackMessage);
}

declare global {
  var __SPATIUS_AVATARVIEW_PROTOTYPE_PATCHED__: boolean | undefined;
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    return String(error.message);
  }

  return String(error);
}

function isAvatarViewNotInitializedError(error: unknown) {
  return getErrorMessage(error).includes("AvatarView not initialized");
}

function isAvatarRenderOutOfMemoryError(error: unknown) {
  const message = getErrorMessage(error);
  return message.includes("computeFrameFlatFromParams failed") && message.includes("Out of memory");
}

function waitForAnimationFrame() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function isAvatarViewReady(view: AvatarView) {
  const runtimeView = view as unknown as {
    isInitialized?: boolean;
    renderSystem?: unknown;
  };

  return Boolean(runtimeView.isInitialized && runtimeView.renderSystem);
}

async function waitForAvatarViewReady(view: AvatarView, timeoutMs = 5000) {
  if (isAvatarViewReady(view)) {
    return;
  }

  const start = performance.now();

  while (!isAvatarViewReady(view)) {
    if (performance.now() - start >= timeoutMs) {
      throw new Error("AvatarView initialization timed out before becoming ready.");
    }

    await waitForAnimationFrame();
  }
}

async function retryTransientAvatarViewInitError<T>(
  operation: () => Promise<T>,
  fallbackValue: T,
  maxAttempts = 4
) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (isAvatarRenderOutOfMemoryError(error)) {
        logger.warn("Spatius avatar render skipped after SDK memory pressure", {
          error: getErrorMessage(error),
        });
        return fallbackValue;
      }

      if (!isAvatarViewNotInitializedError(error)) {
        throw error;
      }

      await waitForAnimationFrame();
    }
  }

  return fallbackValue;
}

function patchAvatarViewPrototype() {
  if (typeof window === "undefined" || globalThis.__SPATIUS_AVATARVIEW_PROTOTYPE_PATCHED__) {
    return;
  }

  const prototype = AvatarView.prototype as AvatarView & {
    generateTransition?: (options: unknown) => Promise<unknown[]>;
    renderFlame?: (keyframeData?: unknown, enableIdleRendering?: boolean) => Promise<void>;
  };
  const originalRenderFlame = prototype.renderFlame;
  const originalGenerateTransition = prototype.generateTransition;

  if (typeof originalRenderFlame === "function") {
    prototype.renderFlame = async function (keyframeData, enableIdleRendering) {
      return retryTransientAvatarViewInitError(
        () => originalRenderFlame.call(this, keyframeData, enableIdleRendering),
        undefined
      );
    };
  }

  if (typeof originalGenerateTransition === "function") {
    prototype.generateTransition = async function (options) {
      return retryTransientAvatarViewInitError(
        () => originalGenerateTransition.call(this, options),
        []
      );
    };
  }

  globalThis.__SPATIUS_AVATARVIEW_PROTOTYPE_PATCHED__ = true;
}

function sameSdkConfiguration(options: UseSpatiusAvatarOptions) {
  const configuration = AvatarSDK.configuration as
    | (NonNullable<typeof AvatarSDK.configuration> & {
        characterApiBaseUrl?: string;
        region?: string;
      })
    | null;

  return (
    AvatarSDK.appId === options.appId &&
    configuration?.region === (options.region ?? "us-west") &&
    configuration?.drivingServiceMode ===
      (options.drivingServiceMode ?? DrivingServiceMode.backend) &&
    configuration?.characterApiBaseUrl === options.characterApiBaseUrl &&
    configuration?.logLevel === options.sdkLogLevel
  );
}

function isAvatarSdkInitialized() {
  return Boolean((AvatarSDK as typeof AvatarSDK & { isInitialized?: boolean }).isInitialized);
}

async function ensureAvatarSdk(options: UseSpatiusAvatarOptions) {
  if (!isAvatarSdkInitialized()) {
    await AvatarSDK.initialize(options.appId, {
      characterApiBaseUrl: options.characterApiBaseUrl,
      drivingServiceMode: options.drivingServiceMode ?? DrivingServiceMode.backend,
      logLevel: options.sdkLogLevel,
      region: options.region ?? "us-west",
    } as Parameters<typeof AvatarSDK.initialize>[1] & {
      characterApiBaseUrl?: string;
      region?: string;
    });
  } else if (!sameSdkConfiguration(options)) {
    throw new Error(
      "AvatarSDK is already initialized with a different configuration. Keep appId and SDK options stable across mounted Spatius avatar providers."
    );
  }

  if (options.sessionToken) {
    AvatarSDK.setSessionToken(options.sessionToken);
  }

  if (options.userId) {
    AvatarSDK.setUserId(options.userId);
  }
}

function createIdleState(): SpatiusAvatarState {
  return {
    conversationState: null,
    downloadProgress: null,
    error: null,
    isConnected: false,
    isLoading: false,
    isPublishingMicrophone: false,
    micTrack: undefined,
    room: null,
    sdkConnectionState: null,
    status: "idle",
  };
}

function isRtcSdkLoggingEnabled() {
  return process.env.NEXT_PUBLIC_ENABLE_RTC_SDK_LOGS === "1";
}

function getEffectiveSdkLogLevel(sdkLogLevel: UseSpatiusAvatarOptions["sdkLogLevel"]) {
  if (sdkLogLevel !== undefined) {
    return sdkLogLevel;
  }

  return isRtcSdkLoggingEnabled() ? undefined : AvatarSdkLogLevel.off;
}

function getEffectivePlayerOptions(playerOptions: UseSpatiusAvatarOptions["playerOptions"]) {
  const defaultLogLevel: AvatarPlayerOptions["logLevel"] | undefined = isRtcSdkLoggingEnabled()
    ? undefined
    : "none";

  if (!playerOptions) {
    return defaultLogLevel ? { logLevel: defaultLogLevel } : undefined;
  }

  if (playerOptions.logLevel !== undefined || !defaultLogLevel) {
    return playerOptions;
  }

  return {
    ...playerOptions,
    logLevel: defaultLogLevel,
  };
}

function getRoomAudioDiagnostics(room: Room | null) {
  const activeAudioPublications = collectActiveRemoteAudioPublications(room);

  return {
    roomName: room?.name || null,
    activeAudioCount: activeAudioPublications.length,
    activeAudioPublications: activeAudioPublications.map((publication) => ({
      participantIdentity: publication.participantIdentity,
      trackSid: publication.trackSid,
    })),
  };
}

function cleanupSpatiusSdkAudioElementsForReason(reason: string) {
  const result = cleanupSpatiusSdkAudioElements();

  if (result.removedCount > 0) {
    logger.warn("Removed Spatius SDK audio elements", {
      reason,
      removedCount: result.removedCount,
      elementIds: result.elementIds,
    });
  }
}

export function useSpatiusAvatar(options: UseSpatiusAvatarOptions): UseSpatiusAvatarResult {
  const {
    appId,
    avatarId,
    characterApiBaseUrl,
    connection,
    drivingServiceMode,
    enabled = true,
    onAvatarError,
    onConnected,
    onDisconnected,
    onLoadProgress,
    onStateChange,
    playerOptions,
    publishMicrophone = false,
    region,
    sdkLogLevel,
    sessionToken,
    userId,
  } = options;
  const { roomName, token, url } = connection;
  const effectiveSdkLogLevel = useMemo(() => getEffectiveSdkLogLevel(sdkLogLevel), [sdkLogLevel]);
  const effectivePlayerOptions = useMemo(
    () => getEffectivePlayerOptions(playerOptions),
    [playerOptions]
  );

  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const [containerReady, setContainerReady] = useState(false);
  const [state, setState] = useState<SpatiusAvatarState>(createIdleState);
  // The view has drawn its first frame (idle animation), which can be well
  // before the session connects: layout can measure her from then on.
  const [viewReady, setViewReady] = useState(false);

  const avatarViewRef = useRef<AvatarView | null>(null);
  // The learner hid the avatar: drawing stays paused (the connection and the
  // room audio continue), and recovery paths must not resume it.
  const renderingHiddenRef = useRef(false);
  const playerRef = useRef<AvatarPlayer | null>(null);
  const reconnectInProgressRef = useRef(false);
  const roomRef = useRef<Room | null>(null);
  const viewerTrackSubscribedHandlerRef = useRef<{
    room: Room;
    handler: () => void;
  } | null>(null);
  const callbacksRef = useRef({
    onAvatarError,
    onConnected,
    onDisconnected,
    onLoadProgress,
    onStateChange,
    playerOptions,
  });

  useEffect(() => {
    callbacksRef.current = {
      onAvatarError,
      onConnected,
      onDisconnected,
      onLoadProgress,
      onStateChange,
      playerOptions: effectivePlayerOptions,
    };
  }, [
    effectivePlayerOptions,
    onAvatarError,
    onConnected,
    onDisconnected,
    onLoadProgress,
    onStateChange,
  ]);

  const tokenSource = useMemo(
    () =>
      TokenSource.literal({
        participantToken: token,
        serverUrl: url,
      }),
    [token, url]
  );

  const baseSession = useSession(tokenSource, {
    room: state.room ?? undefined,
  });

  patchAvatarViewPrototype();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return installSpatiusSdkAudioElementGuard((result) => {
      logger.warn("Silenced Spatius SDK audio elements", {
        reason: "spatius_dom_audio_guard",
        removedCount: result.removedCount,
        elementIds: result.elementIds,
        mutedCount: "mutedCount" in result ? result.mutedCount : 0,
        mutedElementIds: "mutedElementIds" in result ? result.mutedElementIds : [],
      });
    });
  }, [enabled]);

  const updateStatus = useCallback((status: SpatiusAvatarConnectionStatus) => {
    setState((previous) => ({
      ...previous,
      isConnected: status === "connected",
      isLoading: status === "initializing" || status === "connecting",
      status,
    }));

    callbacksRef.current.onStateChange?.(status);
  }, []);

  const syncMicTrack = useCallback((room: Room | null) => {
    const publication = room?.localParticipant.getTrackPublication(Track.Source.Microphone);

    setState((previous) => ({
      ...previous,
      isPublishingMicrophone: Boolean(publication?.track),
      micTrack: publication?.track,
      room,
    }));
  }, []);

  // The Spatius viewer room is render-only. Audible tutor audio is owned by
  // the base lesson room. The SDK's LiveKitProvider attaches a hidden
  // `audio-<identity>` element for subscribed remote audio tracks, so recovery
  // must keep viewer audio unsubscribed instead of merely hiding the element.
  const enforceViewerVideoOnly = useCallback((room: Room | null, reason: string) => {
    if (!room) {
      return;
    }

    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        const shouldSubscribe = shouldSubscribeSpatiusViewerPublication({
          participantIdentity: participant.identity,
          publicationKind: publication.kind,
        });

        try {
          publication.setSubscribed(shouldSubscribe);
        } catch (error) {
          logger.warn("Failed to enforce Spatius viewer video-only subscription", {
            reason,
            participant: participant.identity,
            shouldSubscribe,
            error: getErrorMessage(error),
          });
        }
      }
    }

    const result = silenceSpatiusSdkAudioElementsForLipSync();
    if (result.removedCount > 0 || result.mutedCount > 0) {
      logger.warn("Silenced Spatius SDK audio elements", {
        reason,
        removedCount: result.removedCount,
        elementIds: result.elementIds,
        mutedCount: result.mutedCount,
        mutedElementIds: result.mutedElementIds,
      });
    }
  }, []);

  const wireViewerVideoOnly = useCallback(
    (room: Room | null, reason: string) => {
      const previous = viewerTrackSubscribedHandlerRef.current;
      if (previous) {
        try {
          previous.room.off(RoomEvent.TrackSubscribed, previous.handler);
          previous.room.off(RoomEvent.TrackUnsubscribed, previous.handler);
          previous.room.off(RoomEvent.TrackPublished, previous.handler);
          previous.room.off(RoomEvent.TrackUnpublished, previous.handler);
          previous.room.off(RoomEvent.ParticipantDisconnected, previous.handler);
        } catch {
          // Ignore detach errors on stale viewer rooms.
        }
        viewerTrackSubscribedHandlerRef.current = null;
      }

      if (!room) {
        return;
      }

      const handler = () => {
        enforceViewerVideoOnly(room, `${reason}_viewer_publication_changed`);
      };

      room.on(RoomEvent.TrackSubscribed, handler);
      room.on(RoomEvent.TrackUnsubscribed, handler);
      room.on(RoomEvent.TrackPublished, handler);
      room.on(RoomEvent.TrackUnpublished, handler);
      room.on(RoomEvent.ParticipantDisconnected, handler);
      viewerTrackSubscribedHandlerRef.current = { room, handler };
      enforceViewerVideoOnly(room, reason);
    },
    [enforceViewerVideoOnly]
  );

  const teardownInstance = useCallback(
    async (player: AvatarPlayer | null, view: AvatarView | null) => {
      const room = roomRef.current;
      if (player || view || room) {
        logger.info("Spatius avatar instance teardown started", {
          ...getRoomAudioDiagnostics(room),
        });
      }

      const viewerHandler = viewerTrackSubscribedHandlerRef.current;
      if (viewerHandler) {
        try {
          viewerHandler.room.off(RoomEvent.TrackSubscribed, viewerHandler.handler);
          viewerHandler.room.off(RoomEvent.TrackUnsubscribed, viewerHandler.handler);
          viewerHandler.room.off(RoomEvent.TrackPublished, viewerHandler.handler);
          viewerHandler.room.off(RoomEvent.TrackUnpublished, viewerHandler.handler);
          viewerHandler.room.off(RoomEvent.ParticipantDisconnected, viewerHandler.handler);
        } catch {
          // Ignore detach errors on rooms that are already disconnecting.
        }
        viewerTrackSubscribedHandlerRef.current = null;
      }

      if (playerRef.current === player) {
        playerRef.current = null;
        roomRef.current = null;
      }

      if (avatarViewRef.current === view) {
        avatarViewRef.current = null;
        setViewReady(false);
      }

      try {
        cleanupSpatiusSdkAudioElementsForReason("spatius_teardown_started");
        await player?.disconnect();
      } catch {
        // Ignore teardown errors so unmounts stay predictable.
      }

      try {
        view?.dispose();
      } catch {
        // Ignore teardown errors so unmounts stay predictable.
      }

      cleanupSpatiusSdkAudioElementsForReason("spatius_teardown_completed");

      if (player || view || room) {
        logger.info("Spatius avatar instance teardown completed", {
          roomName: room?.name || null,
        });
      }
    },
    []
  );

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node);
    setContainerReady(node ? node.offsetWidth > 0 && node.offsetHeight > 0 : false);
  }, []);

  const startPublishingMicrophone = useCallback(async () => {
    const player = playerRef.current;

    if (!player) {
      throw new Error("Avatar player is not ready yet.");
    }

    await player.startPublishing();

    const room = player.getNativeClient() as Room | null;
    roomRef.current = room;
    syncMicTrack(room);
  }, [syncMicTrack]);

  const stopPublishingMicrophone = useCallback(async () => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    await player.stopPublishing();
    syncMicTrack(roomRef.current);
  }, [syncMicTrack]);

  const disconnect = useCallback(async () => {
    const player = playerRef.current;
    const view = avatarViewRef.current;

    if (!player && !view) {
      setState(createIdleState());
      return;
    }

    updateStatus("disconnecting");
    await teardownInstance(player, view);
    setState(createIdleState());
  }, [teardownInstance, updateStatus]);

  const session = useMemo(
    () => ({
      ...baseSession,
      end: disconnect,
    }),
    [baseSession, disconnect]
  );

  const reconnect = useCallback(async () => {
    const player = playerRef.current;

    if (!player) {
      throw new Error("Avatar player is not ready yet.");
    }

    updateStatus("connecting");

    try {
      logger.info("Spatius avatar player reconnect started", {
        ...getRoomAudioDiagnostics(roomRef.current),
      });
      cleanupSpatiusSdkAudioElementsForReason("spatius_reconnect_started");
      reconnectInProgressRef.current = true;
      await player.reconnect();
      const room = player.getNativeClient() as Room | null;
      roomRef.current = room;
      syncMicTrack(room);
      wireViewerVideoOnly(room, "spatius_reconnect_completed");
      updateStatus("connected");
      logger.info("Spatius avatar player reconnect completed", {
        ...getRoomAudioDiagnostics(room),
      });
    } catch (error) {
      const normalizedError = toError(error, "Failed to reconnect avatar stream.");
      logger.warn("Spatius avatar player reconnect failed", {
        error: normalizedError.message,
        ...getRoomAudioDiagnostics(roomRef.current),
      });
      setState((previous) => ({
        ...previous,
        error: normalizedError,
        isConnected: false,
        isLoading: false,
        status: "error",
      }));
      callbacksRef.current.onAvatarError?.(normalizedError);
      callbacksRef.current.onStateChange?.("error");
      throw normalizedError;
    } finally {
      reconnectInProgressRef.current = false;
    }
  }, [syncMicTrack, updateStatus, wireViewerVideoOnly]);

  const recoverIdlePlayback = useCallback(async () => {
    const view = avatarViewRef.current;

    if (!view) {
      throw new Error("Avatar view is not ready yet.");
    }

    logger.warn("Spatius avatar visual playback recovery started", {
      status: state.status,
      conversationState: state.conversationState,
      sdkConnectionState: state.sdkConnectionState,
      renderingEnabled:
        typeof view.isRenderingEnabled === "function" ? view.isRenderingEnabled() : null,
      connectionState: playerRef.current?.getConnectionState?.() ?? null,
      ...getRoomAudioDiagnostics(roomRef.current),
    });

    if (!renderingHiddenRef.current) {
      try {
        view.resumeRendering();
      } catch (error) {
        logger.warn("Spatius avatar resumeRendering recovery failed", {
          error: getErrorMessage(error),
        });
      }
    }

    try {
      await view.controller.resume();
    } catch (error) {
      logger.warn("Spatius avatar controller resume recovery failed", {
        error: getErrorMessage(error),
      });
    }
  }, [state.conversationState, state.sdkConnectionState, state.status]);

  useEffect(() => {
    if (!containerElement) {
      return;
    }

    const markReady = () => {
      setContainerReady(containerElement.offsetWidth > 0 && containerElement.offsetHeight > 0);
    };

    const observer = new ResizeObserver(markReady);
    observer.observe(containerElement);
    const frame = requestAnimationFrame(markReady);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [containerElement]);

  useEffect(() => {
    if (!enabled || !containerReady || !containerElement) {
      return;
    }

    let cancelled = false;
    let player: AvatarPlayer | null = null;
    let view: AvatarView | null = null;
    let connectedNotified = false;

    const notifyConnected = (room: Room | null) => {
      if (connectedNotified || cancelled) {
        return;
      }

      connectedNotified = true;
      callbacksRef.current.onConnected?.(room);
    };

    const cleanupHandlers = () => {
      if (!player) {
        return;
      }

      player.off("connected", handleConnected);
      player.off("disconnected", handleDisconnected);
      player.off("error", handleError);
      player.off("stalled", handleStalled);
    };

    const handleConnected = () => {
      if (cancelled || !player) {
        return;
      }

      const room = player.getNativeClient() as Room | null;
      roomRef.current = room;
      syncMicTrack(room);
      wireViewerVideoOnly(room, "spatius_connected");
      logger.info("Spatius avatar player connected", {
        ...getRoomAudioDiagnostics(room),
      });
      setState((previous) => ({
        ...previous,
        conversationState: previous.conversationState ?? AvatarSdkConversationState.idle,
        sdkConnectionState: AvatarSdkConnectionState.connected,
      }));
      updateStatus("connected");
      notifyConnected(room);
    };

    const handleDisconnected = () => {
      if (cancelled) {
        return;
      }

      const room = roomRef.current;
      const reconnectInProgress = reconnectInProgressRef.current;
      logger.info("Spatius avatar player disconnected", {
        reconnectInProgress,
        ...getRoomAudioDiagnostics(room),
      });
      cleanupSpatiusSdkAudioElementsForReason(
        reconnectInProgress ? "spatius_disconnected_during_reconnect" : "spatius_disconnected"
      );
      if (reconnectInProgress) {
        setState((previous) => ({
          ...previous,
          isConnected: false,
          isLoading: true,
          sdkConnectionState: AvatarSdkConnectionState.disconnected,
          status: "connecting",
        }));
        callbacksRef.current.onStateChange?.("connecting");
        return;
      }

      roomRef.current = null;
      setState({
        ...createIdleState(),
        sdkConnectionState: AvatarSdkConnectionState.disconnected,
      });
      callbacksRef.current.onStateChange?.("idle");
      callbacksRef.current.onDisconnected?.();
    };

    const handleError = (error: unknown) => {
      if (cancelled) {
        return;
      }

      const normalizedError = toError(error, "Failed to initialize avatar.");
      setState((previous) => ({
        ...previous,
        error: normalizedError,
        isConnected: false,
        isLoading: false,
        status: "error",
      }));
      callbacksRef.current.onAvatarError?.(normalizedError);
      callbacksRef.current.onStateChange?.("error");
    };

    const handleStalled = async () => {
      if (cancelled || !player) {
        return;
      }

      try {
        logger.warn("Spatius avatar player stalled; recovering without reconnect", {
          ...getRoomAudioDiagnostics(roomRef.current),
        });
        wireViewerVideoOnly(roomRef.current, "spatius_stalled_recovery_started");
        if (!renderingHiddenRef.current) {
          try {
            view?.resumeRendering();
          } catch (error) {
            logger.warn("Spatius stalled recovery resumeRendering failed", {
              error: getErrorMessage(error),
            });
          }
        }

        try {
          await view?.controller.resume();
        } catch (error) {
          logger.warn("Spatius stalled recovery controller resume failed", {
            error: getErrorMessage(error),
          });
        }

        if (cancelled) {
          return;
        }

        const room = player.getNativeClient() as Room | null;
        roomRef.current = room;
        syncMicTrack(room);
        wireViewerVideoOnly(room, "spatius_stalled_recovery_completed");
        updateStatus("connected");
        logger.info("Spatius avatar player stalled recovery completed", {
          ...getRoomAudioDiagnostics(room),
        });
      } catch (error) {
        const normalizedError = toError(error, "Avatar stream stalled and could not recover.");
        logger.warn("Spatius avatar player stalled recovery failed", {
          error: normalizedError.message,
          ...getRoomAudioDiagnostics(roomRef.current),
        });
      }
    };

    const connectAvatar = async () => {
      updateStatus("initializing");
      setState((previous) => ({
        ...previous,
        downloadProgress: null,
        error: null,
      }));

      try {
        await ensureAvatarSdk({
          appId,
          avatarId,
          characterApiBaseUrl,
          connection: {
            roomName,
            token,
            url,
          },
          drivingServiceMode,
          region,
          sdkLogLevel: effectiveSdkLogLevel,
          sessionToken,
          userId,
        });
        if (cancelled) {
          return;
        }

        const avatar = await AvatarManager.shared.load(avatarId, (progress) => {
          callbacksRef.current.onLoadProgress?.(progress);

          setState((previous) => ({
            ...previous,
            downloadProgress:
              progress.type === LoadProgress.downloading
                ? (progress.progress ?? null)
                : progress.type === LoadProgress.completed
                  ? 1
                  : previous.downloadProgress,
          }));
        });

        if (cancelled) {
          return;
        }

        view = new AvatarView(avatar, containerElement);
        await waitForAvatarViewReady(view);
        if (cancelled) {
          await teardownInstance(null, view);
          return;
        }

        player = new AvatarPlayer(new LiveKitProvider(), view, callbacksRef.current.playerOptions);
        const controller = view.controller;

        avatarViewRef.current = view;
        setViewReady(true);
        if (renderingHiddenRef.current) {
          view.pauseRendering();
        }
        playerRef.current = player;

        controller.onConnectionState = (connectionState) => {
          if (cancelled) {
            return;
          }

          setState((previous) => ({
            ...previous,
            sdkConnectionState: connectionState,
          }));
        };

        controller.onConversationState = (conversationState) => {
          if (cancelled) {
            return;
          }

          setState((previous) => ({
            ...previous,
            conversationState,
          }));
        };

        controller.onError = (error) => {
          handleError(error);
        };

        player.on("connected", handleConnected);
        player.on("disconnected", handleDisconnected);
        player.on("error", handleError);
        player.on("stalled", handleStalled);

        updateStatus("connecting");
        logger.info("Spatius avatar player connect started", {
          roomName,
        });
        await player.connect({ roomName, token, url });

        if (cancelled || !player) {
          controller.onConnectionState = null;
          controller.onConversationState = null;
          controller.onError = null;
          cleanupHandlers();
          await teardownInstance(player, view);
          return;
        }

        const room = player.getNativeClient() as Room | null;
        roomRef.current = room;
        syncMicTrack(room);
        updateStatus("connected");
        logger.info("Spatius avatar player connect completed", {
          ...getRoomAudioDiagnostics(room),
        });
        notifyConnected(room);
      } catch (error) {
        const normalizedError = toError(error, "Failed to initialize avatar.");

        if (!cancelled) {
          setState((previous) => ({
            ...previous,
            error: normalizedError,
            isConnected: false,
            isLoading: false,
            status: "error",
          }));
          callbacksRef.current.onAvatarError?.(normalizedError);
          callbacksRef.current.onStateChange?.("error");
        }

        cleanupHandlers();
        await teardownInstance(player, view);
      }
    };

    void connectAvatar();

    return () => {
      cancelled = true;
      if (view) {
        view.controller.onConnectionState = null;
        view.controller.onConversationState = null;
        view.controller.onError = null;
      }
      cleanupHandlers();
      setState(createIdleState());
      void teardownInstance(player, view);
    };
  }, [
    appId,
    avatarId,
    characterApiBaseUrl,
    containerElement,
    containerReady,
    drivingServiceMode,
    enabled,
    region,
    effectiveSdkLogLevel,
    sessionToken,
    syncMicTrack,
    teardownInstance,
    token,
    updateStatus,
    userId,
    roomName,
    url,
    wireViewerVideoOnly,
  ]);

  useEffect(() => {
    if (!enabled || state.status !== "connected") {
      return;
    }

    if (publishMicrophone) {
      let cancelled = false;

      const startPublishingWithRetry = async (attemptsRemaining: number) => {
        try {
          await startPublishingMicrophone();
        } catch (error) {
          const normalizedError = toError(error, "Failed to publish microphone audio.");
          const isTransientNotConnected =
            normalizedError.message.includes("Not connected. Please call connect() first.") &&
            attemptsRemaining > 0;

          if (cancelled) {
            return;
          }

          if (isTransientNotConnected) {
            window.setTimeout(() => {
              if (!cancelled) {
                void startPublishingWithRetry(attemptsRemaining - 1);
              }
            }, 250);
            return;
          }

          setState((previous) => ({
            ...previous,
            error: normalizedError,
            isConnected: false,
            isLoading: false,
            status: "error",
          }));
          callbacksRef.current.onAvatarError?.(normalizedError);
          callbacksRef.current.onStateChange?.("error");
        }
      };

      void startPublishingWithRetry(4);

      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;

    const stopPublishing = async () => {
      try {
        await stopPublishingMicrophone();
      } catch (error) {
        if (cancelled) {
          return;
        }

        const normalizedError = toError(error, "Failed to stop microphone publishing.");
        setState((previous) => ({
          ...previous,
          error: normalizedError,
        }));
        callbacksRef.current.onAvatarError?.(normalizedError);
      }
    };

    void stopPublishing();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    publishMicrophone,
    startPublishingMicrophone,
    state.status,
    stopPublishingMicrophone,
  ]);

  const setRenderingHidden = useCallback((hidden: boolean) => {
    renderingHiddenRef.current = hidden;
    const view = avatarViewRef.current;
    if (!view) {
      return;
    }

    try {
      if (hidden) {
        view.pauseRendering();
      } else {
        view.resumeRendering();
      }
    } catch (error) {
      logger.warn("Spatius avatar rendering toggle failed", {
        hidden,
        error: getErrorMessage(error),
      });
    }
  }, []);

  const getAvatarBounds = useCallback(() => {
    try {
      return avatarViewRef.current?.getBoundingRect() ?? null;
    } catch {
      return null;
    }
  }, []);

  const exportAvatarFrame = useCallback(async () => {
    const view = avatarViewRef.current;
    if (!view || renderingHiddenRef.current) {
      return null;
    }

    try {
      return await view.exportBitmap();
    } catch {
      return null;
    }
  }, []);

  return {
    ...state,
    avatarId,
    connection,
    containerRef: setContainerRef,
    disconnect,
    exportAvatarFrame,
    getAvatarBounds,
    isViewReady: viewReady,
    reconnect,
    recoverIdlePlayback,
    session,
    setRenderingHidden,
    startPublishingMicrophone,
    stopPublishingMicrophone,
  };
}
